import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Server,
  ShieldAlert,
} from "lucide-react";
import { toast } from "react-toastify";
import { adminApi } from "../../../api/adminApi";
import "./EmailSettings.css";

const EMPTY_FORM = {
  host: "",
  port: "22",
  username: "",
  hostKeyFingerprint: "",
  certificateEmail: "",
  authenticationMethod: "privateKey",
  privateKey: "",
  password: "",
  passphrase: "",
};

const NginxServerSettings = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [savedSnapshot, setSavedSnapshot] = useState(
    JSON.stringify(EMPTY_FORM),
  );
  const [connection, setConnection] = useState({
    configured: false,
    authenticationMethod: "privateKey",
    privateKeyConfigured: false,
    passwordConfigured: false,
    passphraseConfigured: false,
    lastTestedAt: null,
  });
  const [connectionTest, setConnectionTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const loadSettings = async () => {
      try {
        const response = await adminApi.getNginxServerSettings();
        if (!active) return;
        const data = response.data || {};
        const next = {
          ...EMPTY_FORM,
          host: data.host || "",
          port: String(data.port || 22),
          username: data.username || "",
          hostKeyFingerprint: data.hostKeyFingerprint || "",
          certificateEmail: data.certificateEmail || "",
          authenticationMethod: data.authenticationMethod || "privateKey",
        };
        setForm(next);
        setSavedSnapshot(JSON.stringify(next));
        setConnection(data);
      } catch (loadError) {
        if (active) {
          setError(
            loadError.response?.data?.error ||
              "Could not load Nginx server settings.",
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    loadSettings();
    return () => {
      active = false;
    };
  }, []);

  const isDirty = useMemo(
    () => JSON.stringify(form) !== savedSnapshot,
    [form, savedSnapshot],
  );
  const isSameSavedConnection =
    connection.configured &&
    form.host.trim() === connection.host &&
    Number(form.port) === connection.port &&
    form.username.trim() === connection.username &&
    form.hostKeyFingerprint.trim() === connection.hostKeyFingerprint &&
    form.authenticationMethod === connection.authenticationMethod;

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setConnectionTest(null);
  };

  const toPayload = () => ({ ...form, port: Number(form.port) });

  const testConnection = async () => {
    setTesting(true);
    setError("");
    setConnectionTest(null);
    try {
      const response = await adminApi.testNginxServerConnection(toPayload());
      setConnectionTest(response.data);
      toast.success(response.message || "SSH connection verified.");
    } catch (testError) {
      const message =
        testError.response?.data?.error ||
        "Nginx server connection test failed.";
      setError(message);
      toast.error(message);
    } finally {
      setTesting(false);
    }
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await adminApi.saveNginxServerSettings(toPayload());
      const data = response.data || {};
      const next = {
        ...EMPTY_FORM,
        host: data.host || form.host,
        port: String(data.port || form.port),
        username: data.username || form.username,
        hostKeyFingerprint:
          data.hostKeyFingerprint || form.hostKeyFingerprint,
        certificateEmail: data.certificateEmail || form.certificateEmail,
        authenticationMethod: data.authenticationMethod || "privateKey",
      };
      setForm(next);
      setSavedSnapshot(JSON.stringify(next));
      setConnection(data);
      setConnectionTest(data.testResult || null);
      toast.success(response.message || "Nginx server connection saved.");
    } catch (saveError) {
      const message =
        saveError.response?.data?.error ||
        "Could not save Nginx server settings.";
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="email-settings-page" aria-busy="true">
        <div className="es-skeleton es-sk-title" />
        <div className="es-skeleton es-sk-card" />
        <span className="es-sr-only">Loading Nginx server settings</span>
      </div>
    );
  }

  return (
    <div className="email-settings-page">
      <header className="es-header">
        <h1>Nginx server connection</h1>
        <p>
          Configure the SSH connection used to create and enable Nginx site
          files for assigned store requests.
        </p>
      </header>

      {error && (
        <div className="es-alert es-alert-error" role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <form className="es-form" onSubmit={saveSettings}>
        <section className="es-card">
          <div className="es-card-head">
            <div className="es-card-head-row">
              <div>
                <h2>SSH access</h2>
                <p>
                  SSH credentials are encrypted before being saved and are
                  never returned to the browser.
                </p>
              </div>
              {connection.configured && (
                <span className="es-badge">Connection configured</span>
              )}
            </div>
          </div>

          <div className="es-group">
            <div className="es-grid">
              <div className="es-field">
                <label htmlFor="nginx-ssh-host">Server hostname</label>
                <input
                  id="nginx-ssh-host"
                  className="es-input"
                  autoComplete="off"
                  value={form.host}
                  onChange={(event) => updateField("host", event.target.value)}
                  placeholder="web01.example.internal"
                  required
                />
              </div>
              <div className="es-field">
                <label htmlFor="nginx-ssh-port">SSH port</label>
                <input
                  id="nginx-ssh-port"
                  className="es-input"
                  type="number"
                  min="1"
                  max="65535"
                  value={form.port}
                  onChange={(event) => updateField("port", event.target.value)}
                  required
                />
              </div>
              <div className="es-field">
                <label htmlFor="nginx-ssh-user">SSH username</label>
                <input
                  id="nginx-ssh-user"
                  className="es-input"
                  autoComplete="username"
                  value={form.username}
                  onChange={(event) =>
                    updateField("username", event.target.value)
                  }
                  required
                />
              </div>
              <div className="es-field">
                <label htmlFor="nginx-ssh-fingerprint">
                  SSH host-key fingerprint (SHA256)
                </label>
                <input
                  id="nginx-ssh-fingerprint"
                  className="es-input"
                  autoComplete="off"
                  value={form.hostKeyFingerprint}
                  onChange={(event) =>
                    updateField("hostKeyFingerprint", event.target.value)
                  }
                  placeholder="SHA256:..."
                  required
                />
              </div>
              <div className="es-field">
                <label htmlFor="nginx-ssh-auth-method">
                  Authentication method
                </label>
                <select
                  id="nginx-ssh-auth-method"
                  className="es-input"
                  value={form.authenticationMethod}
                  onChange={(event) =>
                    updateField("authenticationMethod", event.target.value)
                  }
                >
                  <option value="password">Username and password</option>
                  <option value="privateKey">SSH private key</option>
                </select>
              </div>
            </div>
            <div className="es-field">
              <label htmlFor="nginx-certbot-email">
                Let&apos;s Encrypt / Certbot contact email
              </label>
              <input
                id="nginx-certbot-email"
                className="es-input"
                type="email"
                autoComplete="email"
                value={form.certificateEmail}
                onChange={(event) =>
                  updateField("certificateEmail", event.target.value)
                }
                placeholder="admin@example.com"
                required
              />
              <p className="es-hint">
                Used when requesting or renewing a certificate for a store
                domain. You can update this address here if it changes.
              </p>
            </div>
            {form.authenticationMethod === "password" ? (
              <div className="es-field">
                <label htmlFor="nginx-ssh-password">
                  SSH password{" "}
                  {connection.passwordConfigured && (
                    <span className="es-badge">Saved</span>
                  )}
                </label>
                <input
                  id="nginx-ssh-password"
                  className="es-input"
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(event) =>
                    updateField("password", event.target.value)
                  }
                  placeholder={
                    connection.passwordConfigured &&
                    connection.authenticationMethod === "password"
                      ? "Leave blank to keep the saved password"
                      : "Enter SSH password"
                  }
                  required={
                    !(
                      connection.passwordConfigured &&
                      connection.authenticationMethod === "password"
                    )
                  }
                />
              </div>
            ) : (
              <>
                <div className="es-field">
                  <label htmlFor="nginx-ssh-private-key">
                    SSH private key{" "}
                    {connection.privateKeyConfigured && (
                      <span className="es-badge">Saved</span>
                    )}
                  </label>
                  <textarea
                    id="nginx-ssh-private-key"
                    className="es-input"
                    rows={5}
                    autoComplete="off"
                    value={form.privateKey}
                    onChange={(event) =>
                      updateField("privateKey", event.target.value)
                    }
                    placeholder={
                      connection.privateKeyConfigured &&
                      connection.authenticationMethod === "privateKey"
                        ? "Leave blank to keep the saved key"
                        : "Paste the SSH private key"
                    }
                    required={
                      !(
                        connection.privateKeyConfigured &&
                        connection.authenticationMethod === "privateKey"
                      )
                    }
                  />
                </div>
                <div className="es-field">
                  <label htmlFor="nginx-ssh-passphrase">
                    SSH key passphrase{" "}
                    {connection.passphraseConfigured && (
                      <span className="es-badge">Saved</span>
                    )}
                  </label>
                  <input
                    id="nginx-ssh-passphrase"
                    className="es-input"
                    type="password"
                    autoComplete="new-password"
                    value={form.passphrase}
                    onChange={(event) =>
                      updateField("passphrase", event.target.value)
                    }
                    placeholder={
                      connection.passphraseConfigured
                        ? "Leave blank to keep the saved passphrase"
                        : "Optional"
                    }
                  />
                </div>
              </>
            )}

            <div className="es-alert es-alert-warning" role="note">
              <ShieldAlert size={18} aria-hidden="true" />
              <span>
                Verify the SSH fingerprint out of band. Use a dedicated deploy
                account restricted to the supplied Nginx helper; do not use
                unrestricted root SSH access.
              </span>
            </div>
            <p className="es-hint">
              After SSH access is confirmed, install the provided helper at{" "}
              <code>/usr/local/sbin/tooraflex-nginx-site</code> and grant the
              SSH account non-interactive sudo permission for that helper only.
              This SSH test does not check Nginx or sudo permissions.
            </p>
          </div>

          <div className="es-card-footer">
            <button
              type="button"
              className="es-btn"
              onClick={testConnection}
              disabled={testing || saving}
            >
              {testing ? (
                <span className="es-spinner" aria-hidden="true" />
              ) : (
                <Server size={16} aria-hidden="true" />
              )}
              {testing ? "Testing…" : "Test SSH connection"}
            </button>
            <p className="es-hint">
              Connects to the server and verifies its pinned SSH host key. It
              does not run remote commands or require sudo.
            </p>
          </div>
        </section>

        {(connectionTest || connection.lastTestedAt) && (
          <div className="es-alert" role="status">
            <CheckCircle2 size={18} aria-hidden="true" />
            <span>
              {connectionTest
                ? `SSH connected to ${connectionTest.host}; the server host key was verified.`
                : `Last successful test: ${new Date(
                    connection.lastTestedAt,
                  ).toLocaleString()}`}
            </span>
          </div>
        )}

        <div className="es-savebar" role="region" aria-label="Save settings">
          <span className="es-savebar-status">
            {isDirty ? "Unsaved SSH connection changes" : "SSH settings saved"}
          </span>
          <button
            type="submit"
            className="es-btn es-btn-primary"
            disabled={!isDirty || saving || testing}
          >
            {saving ? (
              <>
                <span className="es-spinner" aria-hidden="true" />
                Testing SSH and saving…
              </>
            ) : (
              <>
                <RefreshCw size={15} aria-hidden="true" />
                Test and save SSH connection
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NginxServerSettings;
